import { create } from 'zustand';
import { persist, createJSONStorage, StateStorage } from 'zustand/middleware';
import { useAuthStore } from './auth.store';

export const THEMES = [
    { value: 'slate', label: 'Slate (Đen Trắng)', color: '#0f172a' },
    { value: 'emerald', label: 'Emerald (Ngọc lục bảo)', color: '#059669' },
    { value: 'blue', label: 'Blue Solid', color: '#2563eb' },
    { value: 'violet', label: 'Violet Deep', color: '#7c3aed' },
    { value: 'red', label: 'Red Danger', color: '#dc2626' },
    { value: 'cyan', label: 'Cyan Ocean', color: '#0891b2' },
    { value: 'orange', label: 'Orange Warm', color: '#ea580c' },
];

export interface UserPreferences {
    menuLayout: 'dual-axis' | 'treeview';
    themeName: string;
    accentColor: string;
    themeMode: 'light' | 'dark' | 'auto';
    density: 'comfortable' | 'compact';
    sidebarMode: 'full' | 'collapsed' | 'auto';
    showWelcome: boolean;
    language: string;
    dateFormat: string;
    currencyDisplay: string;
    tablePageSize: number;
    /** Người dùng đã tự chọn số dòng / trang (ở Cài đặt hoặc trên một danh sách) — chưa chọn thì mỗi danh sách dùng mặc định riêng */
    tablePageSizeSet: boolean;
    notifications: boolean;
    /** Lần đổi cài đặt gần nhất (ms) — bản trình duyệt và bản máy chủ, bản nào mới hơn thì dùng */
    prefsSavedAt: number;
}

export const DEFAULTS: UserPreferences = {
    menuLayout: 'treeview',
    themeName: 'emerald',
    accentColor: '#059669',
    themeMode: 'light',
    density: 'comfortable',
    sidebarMode: 'full',
    showWelcome: true,
    language: 'vi',
    dateFormat: 'DD/MM/YYYY',
    currencyDisplay: 'symbol',
    tablePageSize: 25,
    tablePageSizeSet: false,
    notifications: true,
    prefsSavedAt: 0,
};

interface UserPreferencesStore extends UserPreferences {
    setPreference: <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => void;
    resetPreferences: () => void;
}

/** Apply menu layout attribute */
export function applyMenuLayout(menuLayout: 'dual-axis' | 'treeview') {
    if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-menu-layout', menuLayout);
    }
}

/** Apply a predefined theme by setting data-theme attribute */
export function applyTheme(themeName: string) {
    if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-theme', themeName);
    }
}

/** Apply accent color as CSS custom properties on :root */
export function applyAccentColor(hex: string) {
    if (typeof document !== 'undefined') {
        const r = document.documentElement;
        r.style.setProperty('--apv-accent', hex);
        r.style.setProperty('--apv-sidebar-active-bg', hex);
        r.style.setProperty('--apv-text-link', hex);
        r.style.setProperty('--apv-text-muted', hex);
    }
}

export function applyThemeMode(mode: 'light' | 'dark' | 'auto') {
    if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-theme-mode', mode);
        if (mode === 'dark' || (mode === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }
}

export function applyDensity(density: 'comfortable' | 'compact') {
    if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-density', density);
        if (density === 'compact') {
            document.documentElement.classList.add('density-compact');
        } else {
            document.documentElement.classList.remove('density-compact');
        }
    }
}

const userPartitionedStorage: StateStorage = {
    getItem: (name) => {
        const userId = useAuthStore.getState().user?.id || 'guest';
        const val = localStorage.getItem(`${name}_${userId}`);
        if (val) return val;
        // Migration from v2 if user had explicitly customized preferences
        const oldVal = localStorage.getItem(`apv_user_prefs_v2_${userId}`);
        if (oldVal) {
            try {
                const parsed = JSON.parse(oldVal);
                const s = parsed.state || parsed;
                if (s && (s.themeName !== 'blue' || s.menuLayout !== 'dual-axis')) {
                    return oldVal;
                }
            } catch {
                // ignore
            }
        }
        return null;
    },
    setItem: (name, value) => {
        const userId = useAuthStore.getState().user?.id || 'guest';
        localStorage.setItem(`${name}_${userId}`, value);
    },
    removeItem: (name) => {
        const userId = useAuthStore.getState().user?.id || 'guest';
        localStorage.removeItem(`${name}_${userId}`);
    },
};

export const useUserPreferences = create<UserPreferencesStore>()(
    persist(
        (set) => ({
            ...DEFAULTS,
            setPreference: (key, value) => set({ [key]: value }),
            resetPreferences: () => set({ ...DEFAULTS, prefsSavedAt: Date.now() }),
        }),
        {
            name: 'apv_user_prefs_v3',
            storage: createJSONStorage(() => userPartitionedStorage),
            onRehydrateStorage: () => (state) => {
                // Apply theme immediately after rehydration on client
                if (state) {
                    if (state.menuLayout) applyMenuLayout(state.menuLayout);
                    if (state.themeName) applyTheme(state.themeName);
                    if (state.accentColor) applyAccentColor(state.accentColor);
                    if (state.themeMode) applyThemeMode(state.themeMode);
                    if (state.density) applyDensity(state.density);
                }
            }
        }
    )
);

/** Nạp cài đặt có sẵn (đổi tài khoản, bản máy chủ): không tính là người dùng vừa đổi — không đóng dấu, không lưu */
let suppressPrefsStamp = false;
function setPrefsSilently(next: Partial<UserPreferences>) {
    suppressPrefsStamp = true;
    try {
        useUserPreferences.setState(next);
    } finally {
        suppressPrefsStamp = false;
    }
}

useAuthStore.subscribe((state, prevState) => {
    if (state.user?.id !== prevState.user?.id) {
        const userId = state.user?.id || 'guest';
        try {
            const saved = localStorage.getItem(`apv_user_prefs_v3_${userId}`) || localStorage.getItem(`apv_user_prefs_v2_${userId}`);
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed.state) {
                    // If old state was default blue/dual-axis, upgrade to new DEFAULTS
                    if (parsed.state.themeName === 'blue' && parsed.state.menuLayout === 'dual-axis') {
                        setPrefsSilently(DEFAULTS);
                        return;
                    }
                    setPrefsSilently(parsed.state);
                    return;
                }
            }
            setPrefsSilently(DEFAULTS);
        } catch {
            setPrefsSilently(DEFAULTS);
        }
    }
});

/** Synchronous initial load before React renders */
export function initPreferences() {
    try {
        const userId = useAuthStore.getState().user?.id || 'guest';
        const saved = localStorage.getItem(`apv_user_prefs_v3_${userId}`) || localStorage.getItem(`apv_user_prefs_v2_${userId}`);
        if (saved) {
            const parsed = JSON.parse(saved);
            const prefs = parsed.state || parsed;
            // Upgrade if old default
            const effectiveLayout = (prefs.themeName === 'blue' && prefs.menuLayout === 'dual-axis') ? DEFAULTS.menuLayout : (prefs.menuLayout || DEFAULTS.menuLayout);
            const effectiveTheme = (prefs.themeName === 'blue' && prefs.menuLayout === 'dual-axis') ? DEFAULTS.themeName : (prefs.themeName || DEFAULTS.themeName);
            const effectiveAccent = (prefs.themeName === 'blue' && prefs.menuLayout === 'dual-axis') ? DEFAULTS.accentColor : (prefs.accentColor || DEFAULTS.accentColor);

            applyMenuLayout(effectiveLayout);
            applyTheme(effectiveTheme);
            applyAccentColor(effectiveAccent);
            if (prefs.themeMode) applyThemeMode(prefs.themeMode);
            if (prefs.density) applyDensity(prefs.density);
        } else {
            applyMenuLayout(DEFAULTS.menuLayout);
            applyTheme(DEFAULTS.themeName);
            applyAccentColor(DEFAULTS.accentColor);
            applyThemeMode(DEFAULTS.themeMode);
            applyDensity(DEFAULTS.density);
        }
    } catch {
        // ignore parse errors
    }
}

// Subscribe to changes and apply them to the DOM
useUserPreferences.subscribe((state, prevState) => {
    if (state.menuLayout !== prevState.menuLayout) applyMenuLayout(state.menuLayout);
    if (state.themeName !== prevState.themeName) applyTheme(state.themeName);
    if (state.accentColor !== prevState.accentColor) applyAccentColor(state.accentColor);
    if (state.themeMode !== prevState.themeMode) applyThemeMode(state.themeMode);
    if (state.density !== prevState.density) applyDensity(state.density);
});

// ── Lưu cài đặt theo tài khoản trên máy chủ (04/11/2026) ──
// Trước đây chỉ nằm ở localStorage của trình duyệt: đổi máy / trình duyệt / xoá dữ liệu trang là mất.
// Đăng nhập → nạp cài đặt đã lưu của tài khoản (máy chủ ưu tiên); chưa lưu lần nào → đẩy cài đặt hiện có lên.
// Mỗi lần đổi cài đặt → lưu lên máy chủ (gom các lần đổi liên tiếp).
const PREF_KEYS = Object.keys(DEFAULTS) as (keyof UserPreferences)[];
const pickPrefs = (s: any): Partial<UserPreferences> =>
    Object.fromEntries(PREF_KEYS.filter((k) => s?.[k] !== undefined).map((k) => [k, s[k]])) as Partial<UserPreferences>;

let serverSyncedUserId: string | null = null;
let saveTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleServerSave() {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(async () => {
        saveTimer = null;
        if (!serverSyncedUserId || serverSyncedUserId !== useAuthStore.getState().user?.id) return;
        try {
            const { trpcClient } = await import('@/lib/trpc');
            await (trpcClient as any).users.saveMyPreferences.mutate(pickPrefs(useUserPreferences.getState()));
        } catch {
            // mất mạng: cài đặt vẫn giữ ở trình duyệt, lần đổi sau lưu lại
        }
    }, 600);
}

export async function loadServerPreferences() {
    const userId = useAuthStore.getState().user?.id;
    if (!userId || serverSyncedUserId === userId) return;
    try {
        const { trpcClient } = await import('@/lib/trpc');
        const remote = await (trpcClient as any).users.myPreferences.query();
        if (useAuthStore.getState().user?.id !== userId) return;
        const local = useUserPreferences.getState();
        const remoteAt = Number((remote as any)?.prefsSavedAt) || 0;
        if (remote && typeof remote === 'object' && Object.keys(remote).length > 0 && remoteAt >= (local.prefsSavedAt || 0)) {
            setPrefsSilently({ ...DEFAULTS, ...pickPrefs(remote) });
            serverSyncedUserId = userId;
        } else {
            // Máy chủ chưa có, hoặc bản trình duyệt mới hơn (lần lưu trước bị ngắt do tải lại trang) → đẩy bản trình duyệt lên
            serverSyncedUserId = userId;
            scheduleServerSave();
        }
    } catch {
        // máy chủ chưa sẵn sàng: dùng cài đặt của trình duyệt, lần tải trang sau thử lại
    }
}

useUserPreferences.subscribe((state, prevState) => {
    if (suppressPrefsStamp) return;
    const changed = PREF_KEYS.some((k) => k !== 'prefsSavedAt' && state[k] !== prevState[k]);
    // Đổi cài đặt (không phải nạp từ máy chủ) → đóng dấu thời gian để lần tải sau biết bản nào mới hơn
    if (changed && state.prefsSavedAt === prevState.prefsSavedAt) {
        useUserPreferences.setState({ prefsSavedAt: Date.now() });
        return; // lần setState này gọi lại subscribe và lưu
    }
    if (!serverSyncedUserId || serverSyncedUserId !== useAuthStore.getState().user?.id) return;
    if (changed || state.prefsSavedAt !== prevState.prefsSavedAt) scheduleServerSave();
});

useAuthStore.subscribe((state, prevState) => {
    if (state.user?.id !== prevState.user?.id) {
        serverSyncedUserId = null;
        if (state.user?.id) void loadServerPreferences();
    }
});
